import { AdjustmentMode } from '@local/lib';
import { Card, Divider, SimpleGrid, Title } from '@mantine/core';

import type { QualityFormulaPreviewSummary } from '../qualityFormulaTypes';

import { ADJUSTMENT_MODE_COLOR } from '../qualityFormulaMetadata';
import { ResultField } from './ResultField';
import { formatCurrency, formatNumber } from './utils/resultFormatting';

interface SummaryCardProps {
  summary: QualityFormulaPreviewSummary;
}

// Reuses the one shared Discount/Premium color pair (ADJUSTMENT_MODE_COLOR) rather than a second,
// independently-maintained red/green pair — a value increase reads as a Premium (gain), a
// decrease as a Discount (loss).
const getTotalValueChangeColor = (change: number): string => {
  if (change > 0) return ADJUSTMENT_MODE_COLOR[AdjustmentMode.Premium];
  if (change < 0) return ADJUSTMENT_MODE_COLOR[AdjustmentMode.Discount];
  return 'dimmed';
};

export const SummaryCard: React.FC<SummaryCardProps> = ({ summary }) => {
  const startingTotalValue = summary.startingPrice * summary.startingQuantity;
  const finalTotalValue = summary.finalPrice * summary.finalQuantity;
  const totalValueChange = finalTotalValue - startingTotalValue;

  const finalTotalValueColor = getTotalValueChangeColor(totalValueChange);

  return (
    <Card bg="var(--mantine-color-blue-light)" padding="lg" radius="md" shadow="sm" withBorder>
      <Title mb="sm" order={3} size="h5">
        Overall Impact Summary
      </Title>
      <SimpleGrid cols={2} spacing="md">
        <ResultField label="Starting Qty" value={formatNumber(summary.startingQuantity)} />
        <ResultField label="Final Qty" value={formatNumber(summary.finalQuantity)} />
        <ResultField label="Starting Price" value={formatCurrency(summary.startingPrice)} />
        <ResultField label="Final Price" value={formatCurrency(summary.finalPrice)} />
      </SimpleGrid>
      <Divider my="sm" />
      <SimpleGrid cols={2} spacing="md">
        <ResultField label="Starting Total Value" value={formatCurrency(startingTotalValue)} />
        <ResultField color={finalTotalValueColor} label="Final Total Value" value={formatCurrency(finalTotalValue)} />
      </SimpleGrid>
    </Card>
  );
};
